"use client";

import { useTranslations } from "next-intl";
import { ChannelSelect } from "./channel-select";
import { FooterChannelFlag, FooterLocaleFlag } from "./footer-region-flags";
import { LanguageSelect } from "./language-switcher";
import { ReferenceCurrencySelect } from "./reference-currency-select";
import type { Locale } from "@/config/locale";

const compactSelectClass =
	"h-11 min-h-[44px] w-full min-w-0 flex-1 cursor-pointer appearance-none border-0 bg-transparent py-0 pl-1.5 pr-7 text-[13px] font-medium leading-none text-foreground " +
	"rounded-none focus:outline-none focus:ring-0 focus:ring-offset-0 " +
	"focus-visible:outline-none focus-visible:ring-0";

type ChannelRow = { id: string; name: string; slug: string; currencyCode: string };

export function FooterLocaleRegionControls({
	locale,
	channel,
	countryCode,
	channels,
}: {
	locale: Locale;
	channel: string;
	countryCode?: string | null;
	channels: ChannelRow[];
}) {
	const t = useTranslations("footer");
	const tNav = useTranslations("nav");
	return (
		<div className="flex w-full max-w-md flex-col gap-3 sm:max-w-none sm:items-end">
			<div className="w-full sm:w-auto">
				<p className="text-xs font-medium text-muted-foreground sm:text-right">
					{t("languageLabel")} · {t("regionLabel")}
				</p>
				<p className="text-muted-foreground/90 mt-1.5 max-w-prose text-[11px] leading-snug sm:text-right">
					{t("billingCurrenciesLine")}
				</p>
				<div className="mt-2 flex flex-wrap gap-2 sm:justify-end">
					<div className="border-border/60 flex min-h-[44px] min-w-0 flex-1 items-stretch overflow-hidden rounded-lg border bg-background transition-colors hover:border-border sm:max-w-[15rem]">
						<div className="border-border/60 bg-muted/25 flex shrink-0 items-center justify-center border-r px-2.5">
							<FooterLocaleFlag locale={locale} className="rounded-[3px]" />
						</div>
						<LanguageSelect
							id="footer-locale-select"
							currentLocale={locale}
							showOptionEmoji={false}
							className={compactSelectClass}
							aria-label={t("languageLabel")}
						/>
					</div>

					<div className="border-border/60 flex min-h-[44px] min-w-0 flex-1 items-stretch overflow-hidden rounded-lg border bg-background transition-colors hover:border-border sm:max-w-[15rem]">
						<div className="border-border/60 bg-muted/25 flex shrink-0 items-center justify-center border-r px-2.5">
							<FooterChannelFlag channelSlug={channel} className="rounded-[3px]" />
						</div>
						<ChannelSelect
							id="footer-channel-select"
							channels={channels}
							showOptionEmoji={false}
							className={compactSelectClass}
							aria-label={t("regionLabel")}
							regionLabels={{ eu: tNav("regionEu"), us: tNav("regionUs") }}
						/>
					</div>

					<ReferenceCurrencySelect
						id="footer-reference-currency-select"
						locale={locale}
						channel={channel}
						countryCode={countryCode}
						variant="footer"
						aria-label={t("displayCurrencyLabel")}
					/>
				</div>

				<p className="text-muted-foreground/90 mt-2 max-w-prose text-[11px] leading-relaxed sm:text-right">
					{t("currencyRegionHint")}
				</p>
			</div>
		</div>
	);
}
